import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fop-primary)] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--fop-cta-bg)] text-white shadow hover:bg-[var(--fop-cta-bg-hover)]",
        destructive:
          "bg-[var(--fop-danger)] text-white shadow-sm hover:opacity-90",
        outline:
          "border border-[var(--fop-border-2)] bg-transparent text-[var(--fop-text)] shadow-sm hover:bg-[var(--fop-surface-2)] hover:border-[var(--fop-border-hi)]",
        secondary:
          "bg-[var(--fop-surface-2)] text-[var(--fop-text)] shadow-sm hover:bg-[var(--fop-surface-3)]",
        ghost: "text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]",
        link: "text-[var(--fop-primary)] underline-offset-4 hover:underline",
      },
      size: {
        /* Iter 58 — 44px tap target on EVERY interactive size. No exemptions.
           Per ARCP directive: "44x44 minimum touch targets. No grandfathered
           controls." `xs` and `sm` previously fell below this; now both meet
           the floor. `lg` remains the preferred hero field button. */
        default: "h-11 px-5 py-2 text-base",
        sm: "h-11 rounded-md px-4 text-sm",
        xs: "h-11 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-6 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
